import type { TFunction } from 'i18next'
import { birdieChipsClampedCopy } from 'src/components/BirdieRoundCard/birdieCopy'
import type { MarketConfig, MarketOptionConfig } from 'src/types/domain/GameConfig'
import type { RoundOdds } from 'src/types/domain/RoundOdds'

export type PricedOption = MarketOptionConfig & { disabled: boolean }
export type PricedMarket = Omit<MarketConfig, 'options'> & { options: PricedOption[] }

/**
 * Per-round pricing for the Birdie board. Every multiplier comes from
 * `race_ready.payload.odds` (A28) — never from the template in the game
 * config. An option the round did not price is not bettable; with no odds at
 * all every option is disabled (drawn greyed, never priced).
 *
 * The template multiplier is ZEROED wherever the round did not price the
 * option, rather than left in place as a placeholder. It is a real number Go
 * computed from the template config, so anything reading it downstream — the
 * tile's price text, the per-option ceiling, `totalPotentialWin`'s pill — would
 * quote odds this round will never pay. Zero reaches `optionMaxBets` as "no
 * payout ceiling" (it cannot divide by it) and is never bettable, so it can
 * only ever be read as "unknown", never as a price.
 *
 * Board-local on purpose: the shared ChipPlacementPanel must not learn about
 * round-scoped odds.
 */
export function applyRoundOdds(
  markets: MarketConfig[],
  odds: RoundOdds | undefined,
  numberById?: Map<string, number>
): PricedMarket[] {
  if (!odds) {
    return markets.map((market) => ({
      ...market,
      maxMultiplier: 0,
      options: market.options.map((option) => ({
        ...option,
        bettable: false,
        multiplier: 0,
        disabled: true
      }))
    }))
  }
  const rank = (id: string) => numberById?.get(id) ?? Number.MAX_SAFE_INTEGER
  return markets.map((market) => {
    const options: PricedOption[] = market.options
      .map((option) => {
        const multiplier = odds.multipliers[option.id]
        if (multiplier === undefined || !(multiplier > 0))
          return { ...option, bettable: false, multiplier: 0, disabled: false }
        return { ...option, multiplier, disabled: false }
      })
      .sort((a, b) => rank(a.id) - rank(b.id))
    const priced = options.filter((option) => option.bettable).map((option) => option.multiplier)
    return { ...market, options, maxMultiplier: priced.length > 0 ? Math.max(...priced) : 0 }
  })
}

export interface ClampedChip {
  optionId: string
  from: number
  to: number
}

/** A85 — Repeat reduced some chips to this round's per-option max. */
export function repeatClampNotice(clamped: ClampedChip[], t: TFunction): string | null {
  return clamped.length === 0 ? null : birdieChipsClampedCopy(t, clamped.length)
}
